import React from 'react';
import { SkeletonLine } from 'components/theme/Skeleton';
import AvailabilityCalendar from 'components/AvailabilityCalendar';
import PrevArrowIcon from 'assets/chevron-left';
import NextArrowIcon from 'assets/chevron-right';
import { useIsMobile } from 'WindowDimensionProvider';
import { useTranslation } from 'react-i18next';
import { STATUS_PENDING } from 'types/app';
import moment, { Moment } from 'moment';
import {
  ExperiencesAvailability,
  Title4,
  NextIconCalendar,
  PrevIconCalendar,
} from './styles'; // Adjust the import path as necessary

interface CalendarAvailabilityProps {
  availabilityCalendarNextButton: React.RefObject<HTMLDivElement>;
  availabilityCalendarPrevButton: React.RefObject<HTMLDivElement>;
  experience: { loading: boolean };
  isBookable: boolean;
  isDayHighlighted: (date: Date) => boolean;
  isNarrowView: boolean;
  isRecurring: boolean;
  onNextMonthClick: () => void;
  onPrevMonthClick: () => void;
  searchAvailabilityStatus: string;
  setCalendarLoading: (loading: boolean) => void;
  setSidebarFormValue: (field: string, value: Date) => void;
  startDateCalendar: Date | null;
  setSelectedSessionDateAvailabilityCalendar: (date: Moment) => void;
}

const CalendarAvailability: React.FC<CalendarAvailabilityProps> = ({
  availabilityCalendarNextButton,
  availabilityCalendarPrevButton,
  experience,
  isBookable,
  isDayHighlighted,
  isNarrowView,
  isRecurring,
  onNextMonthClick,
  onPrevMonthClick,
  searchAvailabilityStatus,
  setCalendarLoading,
  setSidebarFormValue,
  startDateCalendar,
  setSelectedSessionDateAvailabilityCalendar,
}) => {
  const isMobile = useIsMobile();
  const { t: translate } = useTranslation();

  if (!isBookable || !isRecurring || isMobile) return null;

  if (experience.loading) {
    return <SkeletonLine />;
  }
  return (
    <ExperiencesAvailability
      role="region"
      aria-label={translate('dateRangeSelector')}
    >
      <div>
        <Title4
          as={'h3' as const}
          className="experience-view__experience-section-title"
        >
          {translate('avail')}
        </Title4>
        {startDateCalendar ? (
          <AvailabilityCalendar
            date={startDateCalendar}
            onChange={(date: Date) => {
              setSidebarFormValue('sessionDate', date);
              setSelectedSessionDateAvailabilityCalendar(moment(date));
            }}
            isLoading={searchAvailabilityStatus === STATUS_PENDING}
            isDayHighlighted={isDayHighlighted}
            isMobile={isMobile || isNarrowView}
            orientation={isMobile || isNarrowView ? 'vertical' : 'horizontal'}
            numberOfMonths={2}
            keepOpenOnDateSelect
            isOutsideRange={(date: Date) => !isDayHighlighted(date)}
            onPrevMonthClick={onPrevMonthClick}
            onNextMonthClick={onNextMonthClick}
            navPrev={
              <PrevIconCalendar
                ref={availabilityCalendarPrevButton}
                onClick={() => setCalendarLoading(true)}
                tabIndex={0}
              >
                <PrevArrowIcon width="30" />
              </PrevIconCalendar>
            }
            navNext={
              <NextIconCalendar
                ref={availabilityCalendarNextButton}
                onClick={() => setCalendarLoading(true)}
                tabIndex={0}
              >
                <NextArrowIcon width="27" />
              </NextIconCalendar>
            }
          />
        ) : (
          <SkeletonLine />
        )}
      </div>
    </ExperiencesAvailability>
  );
};

export default CalendarAvailability;
